import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { Map, MapMarker, MapControls, MarkerContent, MarkerLabel } from '../components/ui/map';
import { FaArrowLeft, FaMapMarkerAlt, FaUser, FaCheckCircle, FaClock, FaRedo } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';
import loadImage from '../assets/imageLoader';

const BuyerTrackingMap = () => {
    const { hireId } = useParams();
    const navigate = useNavigate();
    const [hire, setHire] = useState(null);
    const [jobLocation, setJobLocation] = useState(null);
    const [workerPos, setWorkerPos] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [lastRefresh, setLastRefresh] = useState(new Date());

    const fetchLiveLocation = async () => {
        try {
            // Fetch all hires and find the one we need (or fetch specific)
            const res = await apiClient.get('/hires/my-hires');
            const currentHire = res.data.find(h => h.id === hireId);
            
            if (!currentHire) {
                setError('Contract not found.');
                return;
            }

            setHire(currentHire);
            setLastRefresh(new Date());

            // Set Worker Pos if available
            if (currentHire.current_lat && currentHire.current_lng) {
                setWorkerPos({ lat: currentHire.current_lat, lng: currentHire.current_lng });
            }

            // Set Job Location (Static)
            if (!jobLocation) {
                let dest = null;
                if (currentHire.job_post_id) {
                    const jobRes = await apiClient.get(`/jobs/${currentHire.job_post_id}`);
                    if (jobRes.data.location?.lat) {
                        dest = { lat: jobRes.data.location.lat, lng: jobRes.data.location.lng, address: jobRes.data.location.address };
                    }
                } else {
                    // It's a direct hire, destination is actually the buyer's own location
                    // But for tracking, we just show where it's supposed to be
                    const profileRes = await apiClient.get(`/profiles/${currentHire.buyer_id}`);
                    if (profileRes.data.location?.lat) {
                        dest = { lat: profileRes.data.location.lat, lng: profileRes.data.location.lng, address: profileRes.data.location.granular_location };
                    }
                }
                setJobLocation(dest);
            }

        } catch (err) {
            console.error('Tracking fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLiveLocation();
        // Poll every 15 seconds
        const interval = setInterval(fetchLiveLocation, 15000);
        return () => clearInterval(interval);
    }, [hireId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
            <Loader2 className="size-10 animate-spin text-emerald-600 mb-4" />
            <p className="font-bold text-slate-600">Connecting to Live Feed...</p>
        </div>
    );

    return (
        <div className="h-screen flex flex-col font-sans overflow-hidden">
            {/* Overlay Header */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="p-4 bg-white/90 backdrop-blur shadow-lg rounded-2xl text-slate-800 hover:bg-slate-50 transition-all">
                    <FaArrowLeft />
                </button>
                <div className="flex-1 bg-white/90 backdrop-blur shadow-lg rounded-2xl p-4 border border-emerald-100 flex justify-between items-center">
                    <div>
                        <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Tracking Worker</p>
                        <p className="text-sm font-bold text-slate-800 line-clamp-1">{hire?.worker?.name || 'Worker'}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-black text-slate-400 uppercase">Last Update</p>
                        <p className="text-xs font-bold text-slate-600">{lastRefresh.toLocaleTimeString()}</p>
                    </div>
                </div>
            </div>

            {/* Map Area */}
            <div className="flex-1 relative bg-slate-200">
                <Map 
                    center={workerPos ? [workerPos.lng, workerPos.lat] : [90.4125, 23.8103]} 
                    zoom={14} 
                    className="h-full w-full"
                >
                    <MapControls showZoom showCompass position="bottom-right" />
                    
                    {/* Worker Pin */}
                    {workerPos ? (
                        <MapMarker longitude={workerPos.lng} latitude={workerPos.lat}>
                            <MarkerContent>
                                <div className="relative">
                                    <div className="absolute -inset-4 bg-emerald-500/20 rounded-full animate-ping"></div>
                                    <img 
                                        src={loadImage(hire?.worker?.picture)} 
                                        className="w-12 h-12 rounded-full border-4 border-emerald-500 shadow-xl object-cover bg-white relative z-10" 
                                        alt="Worker" 
                                    />
                                </div>
                            </MarkerContent>
                            <MarkerLabel position="bottom">Worker Location</MarkerLabel>
                        </MapMarker>
                    ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-20 backdrop-blur-sm">
                                <div className="bg-white p-6 rounded-3xl shadow-2xl text-center max-w-xs">
                                    <FaClock className="text-orange-400 text-4xl mx-auto mb-4 animate-pulse" />
                                    <h3 className="font-black text-slate-800 mb-2">Waiting for Signal</h3>
                                    <p className="text-sm text-slate-500">The worker hasn't shared their location yet or is currently offline.</p>
                                </div>
                            </div>
                        )
                    }

                    {/* Job Pin */}
                    {jobLocation && (
                        <MapMarker longitude={jobLocation.lng} latitude={jobLocation.lat}>
                            <MarkerContent>
                                <div className="size-10 bg-slate-900 rounded-2xl border-4 border-white shadow-xl flex items-center justify-center text-white">
                                    <FaMapMarkerAlt size={16} className="text-emerald-400" />
                                </div>
                                <MarkerLabel position="top">Job Site</MarkerLabel>
                            </MarkerContent>
                        </MapMarker>
                    )}
                </Map>
            </div>

            {/* Bottom Info Sheet */}
            <div className="bg-white p-6 rounded-t-[2.5rem] shadow-[0_-10px_40px_rgba(0,0,0,0.1)] border-t border-slate-100 relative z-10">
                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-6"></div>
                <div className="flex items-center gap-4">
                    <img src={loadImage(hire?.worker?.picture)} className="w-16 h-16 rounded-2xl object-cover bg-slate-100" alt="Worker" />
                    <div className="flex-1">
                        <h3 className="text-lg font-black text-slate-800 leading-tight">{hire?.worker?.name}</h3>
                        <p className="text-sm font-bold text-emerald-600 flex items-center gap-1">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span> Live on Map
                        </p>
                    </div>
                    <button 
                        onClick={() => { setLoading(true); fetchLiveLocation(); }}
                        className="p-4 bg-slate-100 text-slate-600 rounded-2xl hover:bg-slate-200 transition-all"
                    >
                        <FaRedo size={14} />
                    </button>
                </div>
                
                <div className="mt-6 flex gap-3">
                    <button onClick={() => navigate('/messages', { state: { startChatWith: { id: hire?.worker?.id, name: hire?.worker?.name } } })} className="flex-1 py-4 bg-slate-900 text-white rounded-2xl font-black text-sm flex items-center justify-center gap-2">
                        Message Worker
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BuyerTrackingMap;
